import { useState, useEffect } from "react";
import Rating from "@mui/material/Rating";
import { Col, Form, Row } from "react-bootstrap";
import { RxStar, RxStarFilled } from "react-icons/rx";

import {
  threeStar,
  emptyString,
  textBoxCharLimit,
  sessionStorageKey,
  customerSurveyConstants,
  customerSurveyLabels,
  oneStar,
  NPS_FIRST_QUESTION_ORDER,
  EMPTY_ARRAY_LENGTH,
} from "../../../constants";
import {
  setItemToStorage,
  SURVEY_INTERACTIONS_TYPE_ENUM,
} from "../../../../../system";
import NextButton from "./shared/nextButton";
import QuestionHeadingLogo from "./shared/questionHeadingLogo";
import CustomerInfoCard from "./shared/customerInfoCard";

const {
  DEFAULT_STAR_COLOR,
  STAR_ICON_SIZE,
  TEXTAREA_ROWS,
  ZERO_RATING,
  FIRST_QUESTION_ORDER,
} = customerSurveyConstants;

export const CustomerStarRanking = (props) => {
  const {
    question,
    brandDetail,
    surveyTheme,
    questionArray,
    customerDetail,
    currentQuestion,
    handleNextButton,
    handleBackButton,
    handleSubmitSurvey,
    setSurveyCompleted,
    setCustomerQuestions,
    isNPSFirstActive,
    isFiveStarOnNPS,
    checkFiveStarOnNPS,
    handleSurveyInteractionEvent,
  } = props;

  const { questionText, questionOrder } = question || {};

  //Initialize states
  const [rating, setRating] = useState(ZERO_RATING);
  const [isInvalid, setIsInvalid] = useState(false);
  const [textBox, setTextBox] = useState(emptyString);

  //Function to handle next button
  const handleNext = () => {
    if (!rating) {
      setIsInvalid(true);
      return;
    }

    //If rating is equal to 0 then show error message else save answer and next question
    if (textBox.length >= textBoxCharLimit) return;
    const updatedQuestions = (questionArray || []).map((questionItem) =>
      questionItem.questionOrder === question.questionOrder
        ? {
            ...questionItem,
            questionOrder: questionItem.questionOrder,
            questionTypeId: questionItem.questionTypeId,
            surveyQuestionId: questionItem?.surveyQuestionId || questionItem.id,
            questionText: questionItem.questionText,
            answer: rating.toString(),
            starRatingTextBox: rating <= Number(threeStar) ? textBox : emptyString,
            isSentimentQuestion: questionItem.isSentimentQuestion,
          }
        : questionItem,
    );
    setCustomerQuestions(updatedQuestions);
    setItemToStorage(sessionStorageKey.Rating, rating);

    //Check if it's the last question OR if it's NPS First mode with 4 or 5 stars on NPS question (questionOrder === -1)
    const shouldComplete =
      isNPSFirstActive &&
      isFiveStarOnNPS &&
      question?.questionOrder === NPS_FIRST_QUESTION_ORDER;

    if (shouldComplete) {
      //Log the survey interaction event before direct submit
      if (handleSurveyInteractionEvent) {
        handleSurveyInteractionEvent(
          SURVEY_INTERACTIONS_TYPE_ENUM.SURVEY_NEXT_QUESTION,
        );
      }

      //If completing early (NPS First with 4 or 5 stars), only send the NPS question
      if (setSurveyCompleted) setSurveyCompleted(true);

      // If NPS First mode with 4 or 5 stars, only send the NPS question
      const questionsToSubmit = updatedQuestions.filter(
        (q) => q.questionOrder === NPS_FIRST_QUESTION_ORDER,
      );

      handleSubmitSurvey(questionsToSubmit);
      return; //Don't call handleNextButton if completing
    }
    handleNextButton();
  };

  //Function to handle next button
  const handleBack = () => {
    handleBackButton();
  };

  //Handle change in star rating
  const handleRating = (val) => {
    if (val !== ZERO_RATING) {
      setIsInvalid(false);
    }
    setRating(val);

    //Check if this is NPS First flow and user gave 5 stars on NPS question
    if (checkFiveStarOnNPS) {
      checkFiveStarOnNPS(val);
    }
  };

  useEffect(() => {
    if (
      Array.isArray(questionArray) &&
      questionArray.length > EMPTY_ARRAY_LENGTH
    ) {
      const value = question?.answer || ZERO_RATING;
      setRating(value);

      const data = question?.starRatingTextBox || emptyString;
      setTextBox(data);
    }
    // eslint-disable-next-line
  }, [question]);

  if (!question) {
    return emptyString;
  }

  return (
    <div
      className="d-flex flex-column question-container"
      aria-label={customerSurveyLabels.STAR_RANKING_ARIA}
    >
      <QuestionHeadingLogo brandDetail={brandDetail} />
      <CustomerInfoCard
        questionArray={questionArray}
        questionOrder={questionOrder}
        customerDetail={customerDetail}
      />
      <Row
        className={`justify-content-center text-center ${
          questionOrder === FIRST_QUESTION_ORDER ? "pt-2 my-auto" : "pt-5"
        }  mx-1`}
      >
        <Col xs={12}>
          <h3
            className=" text-bold lh-base mx-3 question-text"
            aria-label={customerSurveyLabels.QUESTION_TEXT_ARIA}
          >
            {questionText}
          </h3>
          <div className="text-center mt-3">
            <Rating
              name="star-rating"
              value={Number(rating) || ZERO_RATING}
              onChange={(_event, value) => handleRating(value)}
              icon={
                <RxStarFilled
                  size={STAR_ICON_SIZE}
                  color={surveyTheme?.starColor || DEFAULT_STAR_COLOR}
                />
              }
              emptyIcon={
                <RxStar
                  size={STAR_ICON_SIZE}
                  color={surveyTheme?.starColor || DEFAULT_STAR_COLOR}
                />
              }
            />
          </div>

          {isInvalid ? (
            <p
              className="text-danger"
              aria-label={customerSurveyLabels.RATING_REQUIRED_ARIA}
            >
              {customerSurveyLabels.RATING_REQUIRED}
            </p>
          ) : (
            emptyString
          )}
          {Number(rating) >= Number(oneStar) &&
            Number(rating) <= Number(threeStar) && (
              <div className="w-100 px-3 mt-3">
                <Row className="row mt-auto justify-content-center">
                  <Col md={8} className="mb-4">
                    <h5 className="text-start mt-2 mx-2 question-text">
                      {customerSurveyLabels.OPTIONAL}
                    </h5>
                    <Form.Control
                      as="textarea"
                      id="textarea"
                      className={`mb-1 bg-light textbox-textarea rounded border-0`}
                      name={questionOrder}
                      value={textBox}
                      rows={TEXTAREA_ROWS}
                      placeholder={
                        customerSurveyLabels.ADD_COMMENTS_PLACEHOLDER
                      }
                      maxLength={textBoxCharLimit}
                      onChange={(e) => setTextBox(e.target.value)}
                    />
                    <small
                      className={`text-danger float-start ${
                        textBox.length < textBoxCharLimit
                          ? "d-none"
                          : emptyString
                      }`}
                    >
                      {customerSurveyLabels.getCharLimitExceededMessage(
                        textBoxCharLimit,
                      )}
                    </small>
                  </Col>
                </Row>
              </div>
            )}
        </Col>
      </Row>
      <NextButton
        handleNext={handleNext}
        handleBack={handleBack}
        questionNo={currentQuestion + 1}
        questionArray={questionArray}
        currentQuestion={currentQuestion}
        isNPSFirstActive={isNPSFirstActive}
        isFiveStarOnNPS={isFiveStarOnNPS}
        isNPSQuestion={question?.questionOrder === NPS_FIRST_QUESTION_ORDER}
      />
    </div>
  );
};

export default CustomerStarRanking;
